import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ErrorAlert } from "@/components/error-alert";
import {
  advance,
  formatClock,
  remainingMs,
  saveRun,
  type RoutineRun,
} from "@/lib/routine-run";
import { cn } from "@/lib/utils";

/**
 * Countdown over a running Routine's Actions. Time is measured against the
 * wall clock, not by counting ticks, so it survives tab throttling and a
 * reload (state is persisted through saveRun). When the last Action ends,
 * onFinished runs (the caller marks the Activity Done).
 */
export function RoutineTimer({
  run: initial,
  onChange,
  onFinished,
  onStop,
}: {
  run: RoutineRun;
  onChange: (run: RoutineRun) => void;
  onFinished: () => Promise<void>;
  onStop: () => void;
}) {
  const [run, setRun] = useState<RoutineRun>(initial);
  const [now, setNow] = useState(() => Date.now());
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finishedRef = useRef(false);

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinishing(true);
    void onFinished().catch((err: unknown) => {
      setError(
        err instanceof Error ? err.message : "could not mark the routine done",
      );
      finishedRef.current = false;
      setFinishing(false);
    });
  }

  // Tick once a second; advance past elapsed items; finish at the end.
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      const next = advance(run, t);
      if (next === null) {
        finish();
        return;
      }
      if (next !== run) {
        setRun(next);
        saveRun(next);
        onChange(next);
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, onChange]);

  const item = run.items[run.index];
  const left = remainingMs(run, now);
  const total = run.items.reduce((s, a) => s + a.duration_minutes, 0) * 60_000;
  const done =
    run.items.slice(0, run.index).reduce((s, a) => s + a.duration_minutes, 0) *
      60_000 +
    (item.duration_minutes * 60_000 - left);
  const progress = Math.min(100, (done / total) * 100);
  const clock = formatClock(left);

  useEffect(() => {
    const before = document.title;
    document.title = `${clock} · ${item.title} — Leno`;
    return () => {
      document.title = before;
    };
  }, [clock, item.title]);

  function update(next: RoutineRun) {
    setRun(next);
    saveRun(next);
    onChange(next);
  }

  function pauseResume() {
    const t = Date.now();
    if (run.pausedAt === null) {
      update({ ...run, pausedAt: t });
    } else {
      update({
        ...run,
        itemStartedAt: run.itemStartedAt + (t - run.pausedAt),
        pausedAt: null,
      });
    }
  }

  function next() {
    const t = Date.now();
    if (run.index + 1 >= run.items.length) {
      finish();
      return;
    }
    update({
      ...run,
      index: run.index + 1,
      itemStartedAt: t,
      pausedAt: run.pausedAt === null ? null : t,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="routine-timer-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-xl">
        <p
          id="routine-timer-title"
          className="text-xs font-medium tracking-wide text-muted-foreground uppercase"
        >
          {run.routineTitle} · {run.index + 1} of {run.items.length}
        </p>
        <h2 className="mt-2 text-2xl font-semibold">{item.title}</h2>
        <p
          className={cn(
            "mt-4 text-6xl font-semibold tabular-nums tracking-tight",
            run.pausedAt !== null ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {clock}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {run.pausedAt !== null
            ? "Paused"
            : `${item.duration_minutes} min action`}
        </p>
        <div
          className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          aria-hidden
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
        <ol className="mt-4 space-y-1 text-left text-sm">
          {run.items.map((a, i) => (
            <li
              key={`${a.title}-${i}`}
              className={cn(
                "flex items-center gap-2",
                i < run.index && "text-muted-foreground line-through",
                i === run.index && "font-medium",
              )}
            >
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  i < run.index
                    ? "bg-border"
                    : i === run.index
                      ? "bg-primary"
                      : "bg-muted-foreground/40",
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">{a.title}</span>
              <span className="tabular-nums text-muted-foreground">
                {a.duration_minutes} min
              </span>
            </li>
          ))}
        </ol>
        {error && (
          <div className="mt-4">
            <ErrorAlert message={error} />
          </div>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={pauseResume} disabled={finishing}>
            {run.pausedAt !== null ? "Resume" : "Pause"}
          </Button>
          <Button onClick={next} disabled={finishing}>
            {run.index + 1 >= run.items.length ? "Finish" : "Next"}
          </Button>
          <Button variant="ghost" onClick={onStop} disabled={finishing}>
            Stop
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Finishing marks the calendar block Done. Stop leaves it for the
          Planning Session.
        </p>
      </div>
    </div>
  );
}
